/**
 * Calculator page header with title, year selector, and OSD selector
 *
 * When OSD is set from URL context (osdFromContext), shows read-only indicator
 * instead of the full selector - user already chose OSD at the start.
 */
import { Title, Text, Stack, Paper, Group, SegmentedControl, Alert, Badge, Box } from '@mantine/core';
import { IconLock, IconAlertCircle, IconMapPin, IconBolt } from '@tabler/icons-react';
import { OSDSelector, OSDIndicator } from '../../../shared/components/OSDSelector';
import { OSD_METADATA, getAvailableTariffsForOSD } from '../../../utils/osdMetadata';
import type { OSDCode } from '../../../types/osd.types';

interface CalculatorHeaderProps {
  selectedOSD: OSDCode;
  onOSDChange: (osd: OSDCode) => void;
  calculationYear: number;
  onYearChange: (year: number) => void;
  loading: boolean;
  /** OSD from URL context - when set, shows read-only indicator instead of selector */
  osdFromContext?: OSDCode | null;
}

export function CalculatorHeader({
  selectedOSD,
  onOSDChange,
  calculationYear,
  onYearChange,
  loading,
  osdFromContext,
}: CalculatorHeaderProps) {
  // Check if OSD is locked from URL context
  const isOSDLocked = Boolean(osdFromContext);
  return (
    <Stack gap="xl">
      {/* Page Title */}
      <div>
        <Title order={1} mb="sm">
          Kalkulator taryf energetycznych
        </Title>
        <Text size="lg" c="dimmed">
          Porównaj taryfy energetyczne i dowiedz się, która jest dla Ciebie najbardziej opłacalna
        </Text>
      </div>

      {/* Critical Disclaimer */}
      <Alert icon={<IconAlertCircle size={20} />} color="orange" variant="light">
        <Stack gap="sm">
          <Text size="sm" fw={600}>
            ⚠️ Ważne zastrzeżenia - przeczytaj przed użyciem kalkulatora
          </Text>
          <Text size="sm">
            <strong>Charakter narzędzia:</strong> Kalkulator ma charakter wyłącznie informacyjny
            i edukacyjny. Wyniki są szacunkowe i mogą się różnić od faktycznych kosztów energii.
          </Text>
          <Text size="sm">
            <strong>Czynniki wpływające na dokładność:</strong>
          </Text>
          <ul style={{ marginTop: '0.5rem', marginBottom: '0.5rem', paddingLeft: '1.5rem' }}>
            <li style={{ marginBottom: '0.25rem' }}>
              <strong>Dokładność danych wejściowych</strong> - wyniki zależą od podanych przez
              Ciebie danych (zużycie miesięczne, wzorce godzinowe)
            </li>
            <li style={{ marginBottom: '0.25rem' }}>
              <strong>Zmienność zużycia</strong> - rzeczywiste zużycie energii może się różnić
              w zależności od pogody, temperatury, stylu życia
            </li>
            <li style={{ marginBottom: '0.25rem' }}>
              <strong>Indywidualne warunki umowy</strong> - Twoja umowa z dostawcą może zawierać
              dodatkowe opłaty lub rabaty nieuwzględnione w kalkulatorze
            </li>
          </ul>
          <Text size="sm" fw={600} mt="xs">
            <strong>Weryfikacja wymagana:</strong> Przed podjęciem decyzji o zmianie taryfy
            ZAWSZE skontaktuj się z dostawcą energii (Tauron, PGE, Energa, Enea) i sprawdź
            aktualne ceny na stronie URE (https://www.ure.gov.pl).
          </Text>
          <Text size="sm" c="dimmed" style={{ fontStyle: 'italic' }}>
            Korzystając z kalkulatora, akceptujesz, że Taryfy Prądu nie ponosi odpowiedzialności
            za decyzje podejmowane na podstawie wyników kalkulacji.
          </Text>
        </Stack>
      </Alert>

      {/* OSD Section - Read-only indicator when OSD from context, selector otherwise */}
      {isOSDLocked ? (
        <Paper p="md" withBorder radius="md">
          <Stack gap="sm">
            <div>
              <Text fw={500} size="sm" mb={4}>
                Twój operator sieci dystrybucyjnej (OSD)
              </Text>
              <Text size="xs" c="dimmed">
                Wybrałeś operatora na początku. Taryfy i ceny są dostosowane do Twojego OSD.
              </Text>
            </div>
            <Paper p="sm" withBorder radius="sm" bg="gray.0">
              <Group gap="md" justify="space-between">
                <OSDIndicator osd={selectedOSD} showFullName />
                <Group gap="sm">
                  <Badge
                    size="sm"
                    variant="light"
                    color="gray"
                    leftSection={<IconBolt size={12} />}
                  >
                    {getAvailableTariffsForOSD(selectedOSD).length} taryf
                  </Badge>
                  <Group gap={4}>
                    <IconMapPin size={14} style={{ opacity: 0.5 }} />
                    <Text size="xs" c="dimmed">
                      {OSD_METADATA[selectedOSD].coverage.slice(0, 2).join(', ')}
                      {OSD_METADATA[selectedOSD].coverage.length > 2 && '...'}
                    </Text>
                  </Group>
                </Group>
              </Group>
            </Paper>
          </Stack>
        </Paper>
      ) : (
        <OSDSelector
          value={selectedOSD}
          onChange={onOSDChange}
          disabled={loading}
          compact={false}
          showTariffCount={true}
          label="Wybierz swojego operatora sieci (OSD)"
          description="Wybierz operatora systemu dystrybucyjnego, który dostarcza prąd do Twojego domu. Znajdziesz go na fakturze za prąd."
        />
      )}

      {/* Year Selector */}
      <Paper p="md" withBorder>
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start">
            <div>
              <Text fw={600} size="sm" mb={4}>
                Rok kalkulacji
              </Text>
              <Text size="xs" c="dimmed">
                Wybierz rok, dla którego chcesz obliczyć koszty energii
              </Text>
            </div>
            <SegmentedControl
              value={String(calculationYear)}
              onChange={(value) => onYearChange(Number(value))}
              disabled={loading}
              data={[
                {
                  value: '2026',
                  label: (
                    <Group gap={6}>
                      <IconBolt size={14} />
                      <span>2026</span>
                    </Group>
                  ),
                },
                {
                  value: '2025',
                  label: (
                    <Group gap={6}>
                      <IconLock size={14} />
                      <span>2025</span>
                    </Group>
                  ),
                },
              ]}
            />
          </Group>

          {/* Dynamic info based on selected year */}
          {calculationYear === 2026 ? (
            <Alert
              icon={<IconBolt size={18} />}
              color="green"
              variant="light"
              title="Stawki 2026 - aktualne ceny rynkowe"
            >
              <Stack gap="xs">
                <Text size="sm">
                  Kalkulacja używa <strong>aktualnych cen rynkowych</strong> obowiązujących od 1.01.2026:
                </Text>
                <Group gap="lg">
                  <Box>
                    <Text size="xs" c="dimmed">Energia (G11 brutto)</Text>
                    <Text size="sm" fw={600}>0,61 zł/kWh</Text>
                    <Text size="xs" c="green">(-1,6% vs zamrożona 2025)</Text>
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed">Dystrybucja</Text>
                    <Text size="sm" fw={600}>~0,52 zł/kWh</Text>
                    <Text size="xs" c="orange">(+9% vs 2025)</Text>
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed">Łącznie G11</Text>
                    <Text size="sm" fw={600}>~1,13 zł/kWh</Text>
                    <Text size="xs" c="orange">(+3% vs 2025)</Text>
                  </Box>
                </Group>
                <Text size="xs" c="dimmed" mt="xs">
                  Źródło: URE, taryfy {OSD_METADATA[selectedOSD].shortName} zatwierdzone na 2026
                </Text>
              </Stack>
            </Alert>
          ) : (
            <Alert
              icon={<IconLock size={18} />}
              color="blue"
              variant="light"
              title="Stawki 2025 - ceny zamrożone (historyczne)"
            >
              <Stack gap="xs">
                <Text size="sm">
                  Kalkulacja używa <strong>zamrożonych cen energii</strong> które obowiązywały do 31.12.2025:
                </Text>
                <Group gap="lg">
                  <Box>
                    <Text size="xs" c="dimmed">Cena energii (brutto)</Text>
                    <Text size="sm" fw={600}>0,62 zł/kWh</Text>
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed">Status</Text>
                    <Badge color="blue" variant="light" leftSection={<IconLock size={12} />}>
                      Historyczna
                    </Badge>
                  </Box>
                </Group>
                <Text size="xs" c="dimmed" mt="xs">
                  Źródło: URE, taryfy {OSD_METADATA[selectedOSD].shortName} zatwierdzone na 2025
                </Text>
              </Stack>
            </Alert>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
